import { api } from '../client';

export type PurchaseOrderStatus =
  | 'DRAFT'
  | 'SUBMITTED'
  | 'APPROVED'
  | 'REJECTED'
  | 'ORDERED'
  | 'PARTIALLY_RECEIVED'
  | 'RECEIVED'
  | 'CANCELLED';

export interface PurchaseOrderItem {
  id: string;
  purchaseOrderId: string;
  itemId: string;
  item?: { id: string; sku: string; name: string; unit: string; unitCost?: number };
  quantity: number;
  unitPrice?: number;
  receivedQty: number;
}

export interface PurchaseOrder {
  id: string;
  poNumber: string;
  companyId: string;
  company?: { id: string; name: string; displayName: string };
  supplierId: string;
  supplier?: { id: string; name: string; code: string };
  status: PurchaseOrderStatus;
  orderDate?: string;
  expectedDate?: string;
  totalAmount?: number;
  currency: string;
  notes?: string;
  items?: PurchaseOrderItem[];
  _count?: { items: number; shipmentLinks?: number };
  createdAt: string;
  updatedAt: string;
}

export interface PurchaseOrderItemWithShipped extends PurchaseOrderItem {
  shippedQty: number;
}

export interface PurchaseOrderWithShipments extends Omit<PurchaseOrder, 'items'> {
  items: PurchaseOrderItemWithShipped[];
  shipments: Array<{
    id: string;
    shipmentNumber: string;
    trackingNumber?: string;
    status: string;
    carrier?: string;
    estimatedArrival?: string;
    actualArrival?: string;
    origin?: { id: string; name: string; code: string };
    destination?: { id: string; name: string; code: string };
    items: Array<{
      itemId: string;
      item: { sku: string; name: string; unit: string };
      quantityShipped: number;
    }>;
  }>;
}

export interface CreatePurchaseOrderDto {
  companyId: string;
  supplierId: string;
  expectedDate?: string;
  currency?: string;
  notes?: string;
  items?: { itemId: string; quantity: number; unitPrice?: number }[];
}

export interface QueryPurchaseOrderParams {
  search?: string;
  status?: PurchaseOrderStatus;
  supplierId?: string;
  companyId?: string;
  page?: number;
  limit?: number;
}

export interface ReceiveItemsDto {
  locationId: string;
  items: { purchaseOrderItemId: string; receivedQty: number }[];
}

export const purchaseOrdersApi = {
  async getAll(params?: QueryPurchaseOrderParams) {
    const response = await api.get<{
      data: PurchaseOrder[];
      meta: { total: number; page: number; limit: number; totalPages: number };
    }>('/purchase-orders', { params });
    return response.data;
  },

  async getById(id: string) {
    const response = await api.get<PurchaseOrder>(`/purchase-orders/${id}`);
    return response.data;
  },

  async create(data: CreatePurchaseOrderDto) {
    const response = await api.post<PurchaseOrder>('/purchase-orders', data);
    return response.data;
  },

  async update(id: string, data: Partial<CreatePurchaseOrderDto>) {
    const response = await api.patch<PurchaseOrder>(`/purchase-orders/${id}`, data);
    return response.data;
  },

  async addItem(id: string, item: { itemId: string; quantity: number; unitPrice?: number }) {
    const response = await api.post<PurchaseOrder>(`/purchase-orders/${id}/items`, item);
    return response.data;
  },

  async removeItem(poId: string, itemId: string) {
    const response = await api.delete<PurchaseOrder>(`/purchase-orders/${poId}/items/${itemId}`);
    return response.data;
  },

  async submit(id: string) {
    const response = await api.patch<PurchaseOrder>(`/purchase-orders/${id}/submit`);
    return response.data;
  },

  async issue(id: string) {
    const response = await api.patch<PurchaseOrder>(`/purchase-orders/${id}/issue`);
    return response.data;
  },

  async receiveItems(id: string, data: ReceiveItemsDto) {
    const response = await api.post<PurchaseOrder>(`/purchase-orders/${id}/receive`, data);
    return response.data;
  },

  async cancel(id: string) {
    const response = await api.patch<PurchaseOrder>(`/purchase-orders/${id}/cancel`);
    return response.data;
  },

  async delete(id: string) {
    const response = await api.delete(`/purchase-orders/${id}`);
    return response.data;
  },

  async getWithShipments(id: string) {
    const response = await api.get<PurchaseOrderWithShipments>(
      `/purchase-orders/${id}/with-shipments`
    );
    return response.data;
  },
};
